import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import AppBar from '@material-ui/core/AppBar'
import IconButton from '@material-ui/core/IconButton'
import Box from '@material-ui/core/Box'
import Toolbar from '@material-ui/core/Toolbar'
import Badge from '@material-ui/core/Badge'
import MenuIcon from 'components/material-symbol/icons/Menu'
import { IconMenuOpen, IconMenuClose } from 'components/atoms/icons'
import { useTheme } from '@material-ui/core/styles'
import { useLocation } from 'react-router-dom'
import AccountDropdown from 'components/account-dropdown'
import Footer from 'components/footer'
import BrandSwitcher from 'components/shell/brand-switcher'
import ContactButton from 'components/contact-button'
import Sidebar from './sidebar'
import useStyles from './styles'
import { useIsMobile, useIsTablet } from 'lib/hooks'
import { getAssumedUser } from 'lib/auth/impersonation'
import clsx from 'clsx'

export default function AppShell({
  logo = { src: '', alt: '' },
  menuItems = [],
  showBrandSwitcher = true,
  showAccountDropdown = true,
  toolbarContent = null,
  unviewedLeadCount = null,
  showFooter = false,
  children,
}) {
  const assumedUser = getAssumedUser()
  const location = useLocation()
  const classes = useStyles({ assumedUser, showFooter })
  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isTabletNavOpen, setIsTabletNavOpen] = useState(true)

  const handleDrawerToggle = () => {
    if (!isTablet) return
    if (isTablet && !isMobile) setIsTabletNavOpen(!isTabletNavOpen)
    if (isMobile) setIsMobileNavOpen(!isMobileNavOpen)
  }

  useEffect(() => {
    if (isTablet) {
      setIsTabletNavOpen(false)
    } else {
      setIsTabletNavOpen(true)
    }
    if (isMobile) {
      setIsTabletNavOpen(true)
    }
  }, [isTablet, isMobile])

  const drawerContainer =
    window !== undefined ? () => window.document.body : undefined

  return (
    <>
      {theme.palette.body?.background && (
        <Helmet>
          <style>{`body { background-color: ${theme.palette.body.background}; }`}</style>
        </Helmet>
      )}
      <Box display="flex">
        <AppBar position="fixed" square className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            {menuItems.length > 0 && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                className={`${
                  isMobile ? classes.mobileMenuButton : classes.tabletMenuButton
                }`}
              >
                <Badge
                  invisible={!location.pathname.startsWith('/pga-coach')}
                  classes={{
                    badge: clsx(
                      classes.mobileMenuButtonBadge,
                      unviewedLeadCount > 0 && classes.unviewedLeadBadge,
                    ),
                  }}
                  color={unviewedLeadCount > 0 ? 'default' : 'primary'}
                  variant="dot"
                  overlap="rectangular"
                >
                  {isMobile ? (
                    <MenuIcon />
                  ) : (
                    <>
                      {!isTabletNavOpen ? <IconMenuOpen /> : <IconMenuClose />}
                    </>
                  )}
                </Badge>
              </IconButton>
            )}
            {toolbarContent || (
              <>
                {showBrandSwitcher ? (
                  <BrandSwitcher logo={logo} />
                ) : (
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    style={{
                      display: 'block',
                      width:
                        (isMobile
                          ? logo.mobileWidth ?? logo.width
                          : logo.width) ?? '110px',
                      height:
                        (isMobile
                          ? logo.mobileHeight ?? logo.height
                          : logo.height) ?? 'auto',
                    }}
                  />
                )}
                {showAccountDropdown && !isMobile && <Box flexGrow={1} />}
                {location.pathname.includes('messages') && <ContactButton />}
                {showAccountDropdown && <AccountDropdown />}
              </>
            )}
          </Toolbar>
        </AppBar>
        <Sidebar
          menuItems={menuItems}
          isMobile={isMobile}
          isTablet={isTablet}
          isMobileNavOpen={isMobileNavOpen}
          isTabletNavOpen={isTabletNavOpen}
          setIsTabletNavOpen={setIsTabletNavOpen}
          handleDrawerToggle={handleDrawerToggle}
          assumedUser={assumedUser}
          drawerContainer={drawerContainer}
          location={location}
        />
        <main className={classes.mainContent}>
          <div className={classes.toolbarSpacing} />
          {children}
          {showFooter && <Footer />}
        </main>
      </Box>
    </>
  )
}
